/**
 * The story runner (spec 009): plays the story from its start node through
 * spec 020's `StoryRuntime` (`StoryPlayerHost`). At a scene's reveal its
 * choice panel is clickable and the timer runs as spec 008 says; the chosen
 * option follows its edge, a scene without choices follows its unconditional
 * edge. After each scene `nextPreviewStep` decides what the editor shows when
 * the story stops: "End of story", or "This choice isn't connected to a scene
 * yet" for an option without an arrow, each with ↺ Restart.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { Orientation, SceneCompleteEvent, StoryApiResponse, StoryPackageManifest } from '../../../../Shared/src/@types';
import type { StorySceneChangeEvent } from '../../../../Shared/src/runtime/StoryRuntime';
import { nextPreviewStep, type PreviewStep } from '../../utils/previewRunner';
import { StoryPlayerHost, type StoryPlayerHostHandle } from './PlayerHost';
import { formatPreviewTime } from './PreviewTransport';
import styles from '../StoryPreviewPane.module.scss';

export interface RunnerPosition {
  storySceneId: number;
  nodeId: string | null;
  index: number;
  name: string;
}

interface StoryRunnerPreviewProps {
  story: Pick<StoryApiResponse, 'graph'>;
  manifest: StoryPackageManifest;
  orientation: Orientation;
  startAtStorySceneId?: number | null;
  /** ■: the runner stops. */
  onStop: () => void;
  /** The runner moved to another scene. */
  onPosition?: (position: RunnerPosition) => void;
  showTransport?: boolean;
  wrapClassName?: string;
}

export const StoryRunnerPreview: React.FC<StoryRunnerPreviewProps> = ({
  story,
  manifest,
  orientation,
  startAtStorySceneId = null,
  onStop,
  onPosition,
  showTransport = true,
  wrapClassName,
}) => {
  const hostRef = useRef<StoryPlayerHostHandle>(null);
  const [position, setPosition] = useState<RunnerPosition | null>(null);
  const [stopped, setStopped] = useState<PreviewStep | null>(null);
  const [paused, setPaused] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const nodeRef = useRef<string | null>(null);
  const graph = story.graph;
  const scenes = useMemo(() => manifest.sceneMap ?? {}, [manifest]);

  const handleSceneChange = useCallback((e: StorySceneChangeEvent) => {
    const node = graph.nodes.find((n) => n.type !== 'end' && n.storySceneId === e.storySceneId) ?? null;
    nodeRef.current = node?.id ?? null;
    const next: RunnerPosition = { storySceneId: e.storySceneId, nodeId: node?.id ?? null, index: e.index, name: e.sceneName };
    setPosition(next);
    setStopped(null);
    setPaused(false);
    onPosition?.(next);
  }, [graph, onPosition]);

  const handleSceneComplete = useCallback((_storySceneId: number, e: SceneCompleteEvent) => {
    const nodeId = nodeRef.current;
    if (!nodeId) return;
    const step = nextPreviewStep(
      graph,
      scenes,
      nodeId,
      e.exitType === 'auto' || !e.choiceId ? { type: 'auto' } : { type: 'choice', choiceId: e.choiceId },
    );
    if (step.kind !== 'scene') setStopped(step);
  }, [graph, scenes]);

  const restart = useCallback(() => {
    setStopped(null);
    setPaused(false);
    hostRef.current?.restart();
  }, []);

  const length = position ? ((scenes as Record<number, { duration?: number }>)[position.storySceneId]?.duration ?? 0) : 0;
  const pct = length > 0 ? Math.min(100, (playhead / length) * 100) : 0;

  return (
    <>
      <div className={wrapClassName ?? styles.canvasWrap} data-testid="preview-stage">
        <StoryPlayerHost
          ref={hostRef}
          manifest={manifest}
          orientation={orientation}
          startAtStorySceneId={startAtStorySceneId}
          onSceneChange={handleSceneChange}
          onSceneComplete={handleSceneComplete}
          onPlayhead={setPlayhead}
        />
        {stopped?.kind === 'end' && (
          <div className={styles.runnerEndMsg} data-testid="runner-end">
            <div className={styles.runnerEndIcon}>✓</div>
            <p>End of story</p>
            <button className={styles.runnerRestartBtn} onClick={restart}>↺ Restart</button>
          </div>
        )}
        {stopped?.kind === 'unconnected' && (
          <div className={styles.runnerEndMsg} data-testid="runner-unconnected" role="alert">
            <div className={styles.runnerEndIcon}>!</div>
            <p>This choice isn't connected to a scene yet</p>
            <div className={styles.runnerEndActions}>
              <button className={styles.runnerRestartBtn} onClick={restart}>↺ Restart</button>
              <button className={styles.runnerRestartBtn} onClick={onStop}>■ Stop</button>
            </div>
          </div>
        )}
      </div>

      {showTransport && (
        <div className={styles.transport}>
          <div className={styles.runnerMeta}>
            <span className={styles.runnerBreadcrumb} data-testid="runner-scene">
              {position ? `Scene ${position.index + 1}: ${position.name}` : 'Loading…'}
            </span>
            <button
              className={`${styles.controlBtn} ${styles.runnerStopBtn}`}
              onClick={onStop}
              title="Stop story preview"
              aria-label="Stop story preview"
            >
              ■
            </button>
          </div>
          <div className={styles.controls}>
            {paused ? (
              <button className={styles.controlBtn} title="Play" aria-label="Play" onClick={() => { hostRef.current?.resume(); setPaused(false); }} disabled={!!stopped}>▶</button>
            ) : (
              <button className={styles.controlBtn} title="Pause" aria-label="Pause" onClick={() => { hostRef.current?.pause(); setPaused(true); }} disabled={!!stopped}>⏸</button>
            )}
            <span className={styles.timeDisplay} data-testid="preview-time" data-ms={Math.round(playhead)}>
              {formatPreviewTime(playhead)} / {formatPreviewTime(length)}
            </span>
          </div>
          <div className={styles.timeline}>
            <div className={styles.timelineFill} style={{ width: `${pct}%` }} />
            <div className={styles.timelineHead} style={{ left: `${pct}%` }} />
          </div>
        </div>
      )}
    </>
  );
};

export default StoryRunnerPreview;
